// Dose conversion and concordance calculation per protocol
export interface DoseCalculation {
  weeklyDose: number;
  dailyAverage: number;
}

export interface ConcordanceResult {
  tier: 1 | 2 | 3 | 4;
  description: string;
  isAcceptable: boolean;
}

/**
 * Parse a dose combination string into weekly and daily doses.
 * 
 * Handles patterns like:
 * - "7 x 75" → 525 weekly, 75 daily
 * - "4 x 75 + 3 x 88" → 564 weekly, 80.57 daily
 * - "7 x (200+25)" → 1575 weekly, 225 daily
 * - "5 x 200 + 2 x (200+25)" → 1450 weekly, 207.14 daily
 * 
 * FIXED: Previous version split on '+' before checking parentheses,
 * which broke patterns like "7 x (200+25)" into "7 x (200" and "25)".
 * Now handles parenthetical patterns first, then processes remaining parts.
 */
export const parseDoseCombination = (combination: string): DoseCalculation => {
  if (!combination) return { weeklyDose: 0, dailyAverage: 0 };
  
  // Handle zero dose / active observation option
  if (combination === "0 mcg - No treatment (Active observation)") {
    return { weeklyDose: 0, dailyAverage: 0 };
  }
  
  let totalWeeklyDose = 0;
  
  // First, handle all (xxx+yyy) patterns and replace them with calculated values
  // Pattern: N x (A+B) where we need to calculate N * (A+B)
  const parenPattern = /(\d+)\s*x\s*\((\d+)\+(\d+)\)/g;
  let match;
  let processed = combination;
  
  while ((match = parenPattern.exec(combination)) !== null) {
    const [fullMatch, count, dose1, dose2] = match;
    const weeklyForThis = parseInt(count) * (parseInt(dose1) + parseInt(dose2));
    totalWeeklyDose += weeklyForThis;
    // Remove this pattern from further processing
    processed = processed.replace(fullMatch, '');
  }
  
  // Now handle remaining simple patterns: N x D
  // Split on + but only what remains after removing paren patterns
  const remainingParts = processed.split('+').map(p => p.trim()).filter(p => p.length > 0);
  
  for (const part of remainingParts) {
    const simpleMatch = part.match(/(\d+)\s*x\s*(\d+)/);
    if (simpleMatch) {
      const [, count, dose] = simpleMatch;
      totalWeeklyDose += parseInt(count) * parseInt(dose);
    }
  }
  
  return {
    weeklyDose: totalWeeklyDose,
    dailyAverage: totalWeeklyDose / 7
  };
};

/**
 * Calculate concordance tier between clinician and algorithm doses.
 * 
 * Tier definitions per protocol:
 * - Tier 1: Perfect Agreement - Within ±5 mcg daily of primary dose
 * - Tier 2: Clinically Equivalent - Within ±12.5 mcg daily of primary dose
 * - Tier 3: Acceptable Alternative - Within ±12.5 mcg daily of alternative dose
 * - Tier 4: Disagreement - Outside all acceptable ranges
 */
export const calculateTierConcordance = (
  clinicianPrimary: string,
  clinicianAlternative: string,
  algorithmDaily: number
): ConcordanceResult => {
  const clinicianDose = parseDoseCombination(clinicianPrimary);
  const alternativeDose = parseDoseCombination(clinicianAlternative);
  
  const dailyDifference = Math.abs(clinicianDose.dailyAverage - algorithmDaily);
  
  // Tier 1: Perfect Agreement - Within ±5 mcg daily
  if (dailyDifference <= 5) {
    return {
      tier: 1,
      description: "Perfect Agreement - Within ±5 mcg daily",
      isAcceptable: true
    };
  }
  
  // Tier 2: Clinically Equivalent - Within ±12.5 mcg daily
  if (dailyDifference <= 12.5) {
    return {
      tier: 2,
      description: "Clinically Equivalent - Within ±12.5 mcg daily",
      isAcceptable: true
    };
  }
  
  // Tier 3: Acceptable Alternative - Algorithm matches alternative within ±12.5 mcg daily
  if (alternativeDose.weeklyDose > 0) {
    const altDailyDifference = Math.abs(alternativeDose.dailyAverage - algorithmDaily);
    if (altDailyDifference <= 12.5) {
      return {
        tier: 3,
        description: "Acceptable Alternative - Matches alternative within ±12.5 mcg daily",
        isAcceptable: true
      };
    }
  }
  
  // Tier 4: Disagreement
  return {
    tier: 4,
    description: `Disagreement - ${dailyDifference.toFixed(1)} mcg daily difference`,
    isAcceptable: false
  };
};

export const validateDoseCombination = (combination: string): boolean => {
  if (!combination) return false;
  
  // Updated pattern to handle (xxx+yyy) patterns
  const validPattern = /^(\d+\s*x\s*(\d+|\(\d+\+\d+\)))(\s*\+\s*(\d+\s*x\s*(\d+|\(\d+\+\d+\))))*$/;
  return validPattern.test(combination);
};
